import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useI18n } from '@/context/LanguageContext';
import { getStoredConsent, saveConsent, updateConsent } from '@/lib/consent';

export function CookieBanner() {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Show banner only when there is no stored choice (first visit / cleared storage).
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setAnalytics(!!stored.analytics);
      setMarketing(!!stored.marketing);
    }
  }, []);

  const persist = (choice) => {
    saveConsent({ ...choice, ts: Date.now() });
    updateConsent(choice);
    setVisible(false);
    setModalOpen(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const savePreferences = () => persist({ analytics, marketing });

  if (!visible && !modalOpen) return null;

  return (
    <>
      {visible && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5">
          <div
            data-testid="cookie-banner"
            className="pointer-events-auto mx-auto max-w-[760px] rounded-[var(--radius)] border border-border/80 bg-background p-5 shadow-[var(--shadow-md)] sm:p-6"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--inv-terracotta-soft))]">
                <Cookie className="h-5 w-5 text-[hsl(var(--inv-terracotta))]" />
              </span>
              <div className="min-w-0">
                <h2 className="font-heading text-base font-semibold text-[hsl(var(--inv-slate))]">
                  {t('cookie.banner.title')}
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {t('cookie.banner.text')}{' '}
                  <Link
                    to={`/${lang}/cookies`}
                    className="font-medium text-[hsl(var(--inv-blue))] underline underline-offset-2"
                    data-testid="cookie-banner-policy-link"
                  >
                    {t('cookie.banner.policy')}
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={() => setModalOpen(true)}
                data-testid="cookie-banner-manage-preferences-button"
                className="border-border/80"
              >
                {t('cookie.banner.manage')}
              </Button>
              <Button
                variant="secondary"
                onClick={rejectAll}
                data-testid="cookie-banner-reject-all-button"
              >
                {t('cookie.banner.rejectAll')}
              </Button>
              <Button
                onClick={acceptAll}
                data-testid="cookie-banner-accept-all-button"
                className="bg-[hsl(var(--inv-blue))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--inv-blue-2))]"
              >
                {t('cookie.banner.acceptAll')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="cookie-preferences-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('cookie.modal.title')}</DialogTitle>
            <DialogDescription>{t('cookie.modal.desc')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border/70 p-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-heading text-sm font-semibold text-[hsl(var(--inv-slate))]">
                  {t('cookie.modal.necessary.title')}
                </h3>
                <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t('cookie.modal.necessary.status')}
                </span>
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {t('cookie.modal.necessary.desc')}
              </p>
            </div>

            <div className="rounded-lg border border-border/70 p-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-heading text-sm font-semibold text-[hsl(var(--inv-slate))]">
                  {t('cookie.modal.analytics.title')}
                </h3>
                <Switch
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  data-testid="cookie-preferences-analytics-switch"
                />
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {t('cookie.modal.analytics.desc')}
              </p>
            </div>

            <div className="rounded-lg border border-border/70 p-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-heading text-sm font-semibold text-[hsl(var(--inv-slate))]">
                  {t('cookie.modal.marketing.title')}
                </h3>
                <Switch
                  checked={marketing}
                  onCheckedChange={setMarketing}
                  data-testid="cookie-preferences-marketing-switch"
                />
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {t('cookie.modal.marketing.desc')}
              </p>
            </div>
          </div>

          <Separator />

          <DialogFooter>
            <Button
              onClick={savePreferences}
              data-testid="cookie-preferences-save-button"
              className="w-full bg-[hsl(var(--inv-blue))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--inv-blue-2))] sm:w-auto"
            >
              {t('cookie.modal.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CookieBanner;
